import React, { createRef, useEffect, useState } from 'react'
import InputText from '@elements/InputText'
import {
  LoginContent,
  LoginTitle,
  LoginSubTitle,
  RequestDemoLink,
  LoginLink,
  InvalidEmailInformation,
} from '../../styles'
import useEmailValidation from '@hooks/use-email-validation'
import { Button } from '@thriveglobal/thrive-ui-lib'
import { COLORS } from '@enums/colors'
import { useTranslation } from 'react-i18next'
import useAuth from '@hooks/use-auth'
import { STATES } from '@features/LoginModal'
import { trackCustomPageEvent } from '@api/segment/page'
import { SegmentPage } from '@enums/segment'

export enum LOGIN_MODAL_EMAIL_STATES {
  'SIGNIN',
  'SIGNUP',
}

const LoginFailure: React.FC = () => {
  const { t } = useTranslation()
  return (
    <InvalidEmailInformation>
      {t('login_modal_user_does_not_exist')}{' '}
      <LoginLink>{t('login_modal_email_us')}</LoginLink>{' '}
      {t('login_modal_email_us_postfix')}
    </InvalidEmailInformation>
  )
}

const LoginModalEmail: React.FC<{
  transition: (email: string, state?: STATES) => void
}> = ({ transition }) => {
  const { t } = useTranslation()
  const { isEmailValid, validateEmail } = useEmailValidation()
  const [validSignIn, setValidSignIn] = useState(true)
  const [transitionState, updateState] = useState<LOGIN_MODAL_EMAIL_STATES>(
    LOGIN_MODAL_EMAIL_STATES.SIGNIN
  )
  const { emailSignIn, isLoading } = useAuth()

  useEffect(() => trackCustomPageEvent(SegmentPage.signIn), []);

  const inputRef = createRef<HTMLInputElement>()

  const isSignin = transitionState === LOGIN_MODAL_EMAIL_STATES.SIGNIN
  const isSignup = transitionState === LOGIN_MODAL_EMAIL_STATES.SIGNUP

  const onSubmit = async () => {
    const email = inputRef.current?.value || ''
    try {
      const { isOkta } = await emailSignIn(email)
      if (isOkta) return

      if (isSignup) {
        transition(email, STATES.SIGNUP)
        return
      }
      transition(email, STATES.PASSWORD)
    } catch (_) {
      setValidSignIn(false)
    }
  }

  const requestDemo = () => {
    window.location.href = 'mailto:info@thriveglobal.com'
  }

  return (
    <LoginContent>
      {isSignin && (
        <>
          <LoginTitle>{t('login_modal_title')}</LoginTitle>
          <LoginSubTitle>{t('login_modal_sub_title')}</LoginSubTitle>
        </>
      )}

      {isSignup && (
        <>
          <LoginTitle>{t('sign_up_page_1_title')}</LoginTitle>
          <LoginSubTitle>{t('sign_up_page_1_text')}</LoginSubTitle>
        </>
      )}
      <InputText
        ref={inputRef}
        placeholder={t('login_modal_placeholder')}
        onSubmit={onSubmit}
        onChange={validateEmail}
        isValid={false}
        data-testid="modal-input"
        InvalidInfo={isEmailValid || validSignIn ? null : LoginFailure}
      />
      <Button data-testid="modal-submit" loading={isLoading} onClick={onSubmit}>
        {t('login_modal_button')}
      </Button>

      {isSignin && (
        <>
          <RequestDemoLink
            data-testid="signup-link"
            color={COLORS.teal}
            onClick={() => updateState(LOGIN_MODAL_EMAIL_STATES.SIGNUP)}
          >
            {t('sign_up_page_1_title')}
          </RequestDemoLink>
          <RequestDemoLink
            data-testid="request-demo-link"
            onClick={requestDemo}
            color={COLORS.teal}
          >
            {t('login_modal_action_link')}
          </RequestDemoLink>
        </>
      )}
      {isSignup && (
        <RequestDemoLink
          data-testid="signin-link"
          onClick={() => updateState(LOGIN_MODAL_EMAIL_STATES.SIGNIN)}
          color={COLORS.teal}
        >
          {t('login_modal_back_to_login')}
        </RequestDemoLink>
      )}
    </LoginContent>
  )
}

export default LoginModalEmail
